<script>
import { useAlert } from 'dashboard/composables';
import CRMProductsAPI from 'dashboard/api/crm/products.js';
import Modal from 'dashboard/components/Modal.vue';
import DealFormField from 'dashboard/components/crm/DealFormField.vue';

export default {
  name: 'DealMoveForm',
  components: {
    Modal,
    DealFormField,
  },
  props: {
    deal: { type: Object, required: true },
    formConfig: { type: Object, required: true },
    stageType: { type: String, required: true },
    targetStage: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  emits: ['submit', 'cancel'],
  data() {
    return {
      show: true,
      formData: {},
      availableProducts: [],
    };
  },
  computed: {
    formFields() {
      return this.formConfig.fields || [];
    },
    visibleFields() {
      // Filtrar campos que deben ser visibles según condicionales
      return this.formFields.filter(field => this.shouldShowField(field));
    },
    isComexContact() {
      // Detectar si es COMEX por label o teléfono internacional
      if (!this.deal.primary_contact) return false;
      
      const hasComexLabel = this.deal.primary_contact.labels?.includes('comex');
      const hasInternationalPhone = this.deal.primary_contact.phone_number && 
                                    !this.deal.primary_contact.phone_number.startsWith('+54');
      
      return hasComexLabel || hasInternationalPhone;
    },
    isValid() {
      return this.visibleFields.every(field => {
        if (!field.required) return true;
        const value = this.formData[field.key];
        
        // Para multiselect, verificar que haya al menos una opción seleccionada
        if (field.type === 'multiselect') {
          if (!Array.isArray(value) || value.length === 0) return false;
        } else {
          if (value === null || value === undefined || value === '') return false;
        }
        
        // Si el campo tiene allowOther y "Otro" está seleccionado,
        // validar que el campo de texto de "Otro" no esté vacío
        if (field.allowOther) {
          const otherSelected = field.type === 'select' 
            ? value === 'Otro'
            : (Array.isArray(value) && value.includes('Otro'));
          
          if (otherSelected) {
            const otherText = this.formData[`${field.key}_other_specify`];
            if (!otherText || otherText.trim() === '') return false;
          }
        }
        
        return true;
      });
    },
  },
  async mounted() {
    // Cargar productos disponibles si hay campos que los necesitan
    const hasProductField = this.formFields.some(f => f.updates_deal_field === 'product_ids');
    
    if (hasProductField) {
      await this.loadAvailableProducts();
    }
    
    // Inicializar formData con valores por defecto
    this.formFields.forEach(field => {
      if (field.type === 'multiselect') {
        this.formData[field.key] = [];
      } else if (field.type === 'checkbox') {
        this.formData[field.key] = field.default || false;
      } else {
        this.formData[field.key] = field.default || '';
      }
      
      // Inicializar campo de texto para "Otro" si allowOther está activado
      if (field.allowOther) {
        this.formData[`${field.key}_other_specify`] = '';
      }
      
      // Pre-poblar si el campo actualiza un campo del deal
      if (field.updates_deal_field) {
        const dealValue = this.getPrePopulatedDealValue(field.updates_deal_field);
        if (dealValue !== null && dealValue !== undefined) {
          this.formData[field.key] = dealValue;
        }
      }
    });
  },
  methods: {
    async loadAvailableProducts() {
      try {
        const response = await CRMProductsAPI.get();
        this.availableProducts = response.data || [];
      } catch (error) {
        this.availableProducts = [];
      }
    },
    
    getPrePopulatedDealValue(dealFieldName) {
      switch (dealFieldName) {
        case 'amount_cents':
          return this.deal.amount_cents ? this.deal.amount_cents / 100 : 0;
          
        case 'expected_close_date':
          return this.deal.expected_close_date || '';
          
        case 'payment_method_ids':
          // Soportar tanto payment_method_ids (nuevo, array) como payment_method (legacy, string)
          if (this.deal.payment_method_ids && Array.isArray(this.deal.payment_method_ids) && this.deal.payment_method_ids.length > 0) {
            const options = [
              'Transferencia',
              'Efectivo',
              'Tarjeta de débito',
              'Tarjeta de crédito',
              'QR',
              'Cheque Electrónico'
            ];
            // Retornar array de objetos {id, name} para MultiSelect
            return this.deal.payment_method_ids.map(id => ({
              id: id,
              name: options[id] || ''
            })).filter(item => item.name !== '');
          }
          
          // Si tiene payment_method (string del enum legacy), convertir a array de objetos
          if (this.deal.payment_method) {
            const enumToIndex = {
              'bank_transfer': 0,
              'cash': 1,
              'debit_card': 2,
              'credit_card': 3,
              'qr_code': 4,
              'electronic_check': 5
            };
            const index = enumToIndex[this.deal.payment_method];
            const options = [
              'Transferencia',
              'Efectivo',
              'Tarjeta de débito',
              'Tarjeta de crédito',
              'QR',
              'Cheque Electrónico'
            ];
            return index !== undefined && options[index] ? [{ id: index, name: options[index] }] : [];
          }
          return [];
          
        case 'product_ids':
          // Pre-cargar productos completos del deal (no solo IDs)
          if (this.deal.products && Array.isArray(this.deal.products) && this.deal.products.length > 0) {
            return this.deal.products.map(product => ({
              id: product.id,
              name: product.name + (product.formatted_price ? ' - ' + product.formatted_price : '')
            }));
          }
          return [];
          
        default:
          return null;
      }
    },
    
    shouldShowField(field) {
      // Si no tiene condicional, siempre mostrar
      if (!field.conditional_on) return true;
      
      const { field: parentKey, value: expectedValue, includes: expectedIncludes } = field.conditional_on;
      const parentValue = this.formData[parentKey];
      
      // Si tiene "includes", verificar si el array contiene el valor
      if (expectedIncludes) {
        return Array.isArray(parentValue) 
          ? parentValue.includes(expectedIncludes)
          : parentValue === expectedIncludes;
      }
      
      // Si tiene "value", verificar igualdad exacta
      if (expectedValue) {
        return parentValue === expectedValue;
      }
      
      return true;
    },
    
    getFieldOptions(field) {
      // Delegado a DealFormField — este método se mantiene solo para handleSubmit
      // que necesita resolver opciones para dealUpdates de payment_method_ids.
      if (field.updates_deal_field === 'payment_method_ids') {
        return (field.options || []).map((name, index) => ({ id: index, name }));
      }
      return field.options || [];
    },

    shouldShowOtherTextField(field) {
      if (!field.allowOther) return false;
      const value = this.formData[field.key];
      if (field.type === 'select') return value === 'Otro';
      if (field.type === 'multiselect') return Array.isArray(value) && value.includes('Otro');
      return false;
    },

    handleSubmit() {
      if (!this.isValid) {
        useAlert(this.$t('CRM.DEAL_MOVE_FORM.VALIDATION_ERROR'));
        return;
      }
      
      // Separar campos normales de campos que actualizan el deal
      const dealUpdates = {};
      const formDataClean = {};
      
      this.visibleFields.forEach(field => {
        const value = this.formData[field.key];
        
        if (field.updates_deal_field) {
          // Este campo actualiza un campo del deal
          if (field.updates_deal_field === 'amount_cents' && typeof value === 'number') {
            dealUpdates.amount_cents = Math.round(value * 100);
          } else if (field.updates_deal_field === 'payment_method_ids') {
            // Para métodos de pago, extraer solo los IDs del array de objetos {id, name}
            if (Array.isArray(value) && value.length > 0) {
              dealUpdates.payment_method_ids = value.map(item => item.id);
            }
          } else if (field.updates_deal_field === 'product_ids') {
            // Para productos, extraer solo los IDs del array de objetos {id, name}
            if (Array.isArray(value) && value.length > 0) {
              dealUpdates.product_ids = value.map(item => item.id);
            }
          } else {
            dealUpdates[field.updates_deal_field] = value;
          }
        }
        
        // Siempre incluir en formData para auditoría
        formDataClean[field.key] = value;
        
        // Si el campo tiene allowOther y "Otro" fue seleccionado, incluir el texto especificado
        if (field.allowOther && this.shouldShowOtherTextField(field)) {
          formDataClean[`${field.key}_other_specify`] = this.formData[`${field.key}_other_specify`];
        }
      });
      
      this.$emit('submit', {
        formData: formDataClean,
        dealUpdates,
      });
    },
    
    handleCancel() {
      this.$emit('cancel');
    },
    
    getFieldLabel(field) {
      return field.label || this.$t(`CRM.DEAL_MOVE_FORM.FIELDS.${field.key.toUpperCase()}`);
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="handleCancel">
    <div class="p-6">
      <h3 class="text-lg font-semibold mb-2 text-slate-700 dark:text-slate-200">
        {{ $t('CRM.DEAL_MOVE_FORM.TITLE', { stage: targetStage.name }) }}
      </h3>

      <p class="text-sm text-slate-600 dark:text-slate-300 mb-4">
        {{ $t('CRM.DEAL_MOVE_FORM.DESCRIPTION') }}
      </p>

      <div class="space-y-4">
        <div v-for="field in visibleFields" :key="field.key">
          <label
            :for="field.key"
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            {{ getFieldLabel(field) }}
            <span v-if="field.required" class="text-red-600">*</span>
          </label>

          <DealFormField
            :field="field"
            :model-value="formData[field.key]"
            :other-specify-value="formData[`${field.key}_other_specify`] || ''"
            :available-products="availableProducts"
            :is-comex-contact="isComexContact"
            @update:model-value="formData[field.key] = $event"
            @update:other-specify-value="formData[`${field.key}_other_specify`] = $event"
          />

          <p
            v-if="field.help"
            class="text-xs text-slate-500 dark:text-slate-400 mt-1"
          >
            {{ field.help }}
          </p>
        </div>

        <div
          class="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200 dark:border-n-weak"
        >
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-n-solid-2 rounded-md hover:bg-slate-300 dark:hover:bg-n-solid-3"
            @click="handleCancel"
          >
            {{ $t('CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="!isValid || loading"
            class="px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            @click="handleSubmit"
          >
            <span
              v-if="loading"
              class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
            />
            {{ loading ? $t('CRM.DEAL_MOVE_FORM.PROCESSING') : $t('CRM.DEAL_MOVE_FORM.SUBMIT') }}
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
/* Minimal custom styles, prefer Tailwind */
</style>

